import React, { useState, useEffect, useCallback } from 'react';
import {
  Send, Users, User, Search, CheckCircle2, Circle,
  Mail, Clock, AlertCircle, ChevronLeft, Inbox,
} from 'lucide-react';
import UsersService from './home/homeservice';

const API_BASE_URL = 'https://api.jamboricg.com';

async function fetchPromos(token) {
  const r = await fetch(`${API_BASE_URL}/utils/api/v1/promos/`, {
    headers: { Authorization: `Token ${token}` },
  });
  if (!r.ok) throw new Error('Failed to load history');
  return r.json();
}

async function sendPromo(token, payload) {
  const r = await fetch(`${API_BASE_URL}/utils/api/v1/promos/`, {
    method: 'POST',
    headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || 'Failed to send');
  return data;
}

function getToken() {
  try { return localStorage.getItem('token') || sessionStorage.getItem('token') || ''; }
  catch { return ''; }
}

// ── Toast ──────────────────────────────────────────────────────────────────────
function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  const bg = type === 'success' ? 'bg-teal-600' : 'bg-red-500';
  return (
    <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg text-white text-sm ${bg}`}>
      {type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
      {msg}
      <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100">×</button>
    </div>
  );
}

// ── History row ────────────────────────────────────────────────────────────────
function PromoRow({ p }) {
  return (
    <div className="flex items-start gap-4 px-5 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50">
      <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Mail size={15} className="text-teal-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 truncate">{p.subject}</p>
        <p className="text-xs text-slate-400 mt-0.5">
          {p.recipient_count} recipient{p.recipient_count !== 1 ? 's' : ''} ·{' '}
          {p.sent_at ? new Date(p.sent_at).toLocaleString() : '—'}
        </p>
      </div>
      <span className="text-xs px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-medium flex-shrink-0">
        {p.status}
      </span>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
export default function Mailing() {
  const [tab, setTab] = useState('compose'); // 'compose' | 'history'
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sendToAll, setSendToAll] = useState(true);

  // specific users state
  const [userSearch, setUserSearch] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // history
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // send state
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);

  const token = getToken();

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try { setHistory(await fetchPromos(token)); }
    catch { setToast({ msg: 'Could not load history', type: 'error' }); }
    finally { setHistoryLoading(false); }
  }, [token]);

  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab, loadHistory]);

  // debounced user search
  useEffect(() => {
    if (sendToAll) return;
    const t = setTimeout(async () => {
      setUsersLoading(true);
      try {
        const r = await UsersService.getUsersWithPagination(1, 30, userSearch);
        setAllUsers(r.users || r.results || r || []);
      } catch {
        setAllUsers([]);
      } finally {
        setUsersLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [userSearch, sendToAll]);

  const toggleUser = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSend = async () => {
    if (!subject.trim()) return setToast({ msg: 'Subject is required', type: 'error' });
    if (!message.trim()) return setToast({ msg: 'Message is required', type: 'error' });
    if (!sendToAll && selectedIds.size === 0) return setToast({ msg: 'Select at least one user', type: 'error' });

    setSending(true);
    try {
      const result = await sendPromo(token, {
        subject: subject.trim(),
        message: message.trim(),
        send_to_all: sendToAll,
        user_ids: sendToAll ? [] : [...selectedIds],
      });
      setToast({ msg: `Sent to ${result.recipient_count} recipient${result.recipient_count !== 1 ? 's' : ''}`, type: 'success' });
      setSubject('');
      setMessage('');
      setSelectedIds(new Set());
    } catch (e) {
      setToast({ msg: e.message, type: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-slate-800">Mailing &amp; Promos</h2>
        <p className="text-sm text-slate-400 mt-0.5">Compose and send messages to users</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mb-6 w-fit">
        {[
          { id: 'compose', label: 'Compose', icon: <Send size={14} /> },
          { id: 'history', label: 'History', icon: <Inbox size={14} /> },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === t.id ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {/* ── Compose ─────────────────────────────────────────────────────────── */}
      {tab === 'compose' && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-6 space-y-5">

            {/* Subject */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="e.g. Special offer for our members"
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-400"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Message</label>
              <textarea
                rows={6}
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Write your message here…"
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none"
              />
            </div>

            {/* Recipients toggle */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">Recipients</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setSendToAll(true)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                    sendToAll
                      ? 'bg-teal-600 border-teal-600 text-white'
                      : 'border-slate-200 text-slate-600 hover:border-teal-300'
                  }`}
                >
                  <Users size={14} /> All users
                </button>
                <button
                  onClick={() => { setSendToAll(false); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                    !sendToAll
                      ? 'bg-teal-600 border-teal-600 text-white'
                      : 'border-slate-200 text-slate-600 hover:border-teal-300'
                  }`}
                >
                  <User size={14} /> Specific users
                </button>
              </div>
            </div>

            {/* Specific user picker */}
            {!sendToAll && (
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="p-3 border-b border-slate-100 flex items-center gap-2 bg-slate-50">
                  <Search size={14} className="text-slate-400" />
                  <input
                    type="text"
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    placeholder="Search users by name or email…"
                    className="flex-1 bg-transparent text-sm text-slate-700 focus:outline-none placeholder-slate-400"
                  />
                  {selectedIds.size > 0 && (
                    <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-medium">
                      {selectedIds.size} selected
                    </span>
                  )}
                </div>
                <div className="max-h-56 overflow-y-auto divide-y divide-slate-50">
                  {usersLoading ? (
                    <div className="py-6 text-center text-sm text-slate-400">Searching…</div>
                  ) : allUsers.length === 0 ? (
                    <div className="py-6 text-center text-sm text-slate-400">
                      {userSearch ? 'No users match your search' : 'Start typing to search users'}
                    </div>
                  ) : (
                    allUsers.map(u => {
                      const selected = selectedIds.has(u.id);
                      return (
                        <button
                          key={u.id}
                          onClick={() => toggleUser(u.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors ${selected ? 'bg-teal-50' : ''}`}
                        >
                          {selected
                            ? <CheckCircle2 size={16} className="text-teal-600 flex-shrink-0" />
                            : <Circle size={16} className="text-slate-300 flex-shrink-0" />
                          }
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">
                              {u.first_name || u.last_name ? `${u.first_name} ${u.last_name}`.trim() : u.username}
                            </p>
                            <p className="text-xs text-slate-400 truncate">{u.email}</p>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Send button */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end">
            <button
              onClick={handleSend}
              disabled={sending}
              className="flex items-center gap-2 px-6 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white rounded-xl text-sm font-semibold transition-all"
            >
              {sending ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending…</>
              ) : (
                <><Send size={15} /> Send email</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── History ─────────────────────────────────────────────────────────── */}
      {tab === 'history' && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">Sent promos &amp; notifications</h3>
            <button onClick={loadHistory} className="text-xs text-teal-600 hover:underline">Refresh</button>
          </div>
          {historyLoading ? (
            <div className="py-12 text-center text-sm text-slate-400">Loading…</div>
          ) : history.length === 0 ? (
            <div className="py-12 text-center">
              <Clock size={32} className="mx-auto mb-3 text-slate-200" />
              <p className="text-sm text-slate-400">No messages sent yet</p>
            </div>
          ) : (
            history.map(p => <PromoRow key={p.id} p={p} />)
          )}
        </div>
      )}
    </div>
  );
}
